import React, { useEffect, useState } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Stack, Alert, Divider, Box,
  TextField, Autocomplete, CircularProgress,
} from "@mui/material";
import axios from "../../../config/axiosConfig";

const hoyISO = () => new Date().toISOString().slice(0, 10);
const initialForm = {
  nombre: "", descripcion: "", fecha: hoyISO(),
  monto_contrato: "", potencia_instalada: "", gestion: "", respaldo: "", responsable_tecnico: "",
};

const RegistrarProyectoDialog = ({ open, onClose, onRegistrado }) => {
  const [form, setForm] = useState(initialForm);
  const [cliente, setCliente] = useState(null);
  const [clienteInput, setClienteInput] = useState("");
  const [opcionesCliente, setOpcionesCliente] = useState([]);
  const [buscandoCliente, setBuscandoCliente] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setForm(initialForm);
    setCliente(null);
    setClienteInput("");
    setOpcionesCliente([]);
    setError("");
  }, [open]);

  useEffect(() => {
    if (!clienteInput || clienteInput.trim().length < 2) {
      setOpcionesCliente([]);
      return;
    }
    setBuscandoCliente(true);
    const timer = setTimeout(() => {
      axios.get("/api/proyectos/buscar-clientes", { params: { q: clienteInput } })
        .then((res) => setOpcionesCliente(res.data?.data || []))
        .catch(() => setOpcionesCliente([]))
        .finally(() => setBuscandoCliente(false));
    }, 300);
    return () => clearTimeout(timer);
  }, [clienteInput]);

  const handleChange = (campo) => (e) => setForm((prev) => ({ ...prev, [campo]: e.target.value }));

  const handleGuardar = async () => {
    setSaving(true);
    setError("");
    try {
      const res = await axios.post("/api/proyectos", {
        nombre: form.nombre.trim(),
        descripcion: form.descripcion.trim(),
        fecha: form.fecha,
        cod_cliente: cliente.cod_servicio_cliente,
        monto_contrato: form.monto_contrato || null,
        potencia_instalada: form.potencia_instalada.trim(),
        gestion: form.gestion.trim(),
        respaldo: form.respaldo.trim(),
        responsable_tecnico: form.responsable_tecnico.trim(),
      });
      onRegistrado(res.data?.id);
    } catch (e) {
      setError(e?.response?.data?.message || "Error al registrar el proyecto.");
    } finally {
      setSaving(false);
    }
  };

  const puedeGuardar = form.nombre.trim() && form.descripcion.trim() && form.fecha && cliente && !saving;

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 800 }}>Registrar Proyecto</DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}
        <Stack spacing={1.5}>
          <TextField
            size="small" label="Nombre" required
            value={form.nombre} onChange={handleChange("nombre")}
          />
          <TextField
            size="small" label="Descripción" required
            value={form.descripcion} onChange={handleChange("descripcion")}
          />
          <TextField
            size="small" type="date" label="Fecha" required
            InputLabelProps={{ shrink: true }}
            value={form.fecha} onChange={handleChange("fecha")}
          />
          <Autocomplete
            size="small"
            options={opcionesCliente}
            loading={buscandoCliente}
            value={cliente}
            inputValue={clienteInput}
            filterOptions={(x) => x}
            getOptionLabel={(o) => (o ? `${o.razon_social} / ${o.descripcion}` : "")}
            isOptionEqualToValue={(o, v) => o.cod_servicio_cliente === v?.cod_servicio_cliente}
            onChange={(_, value) => setCliente(value)}
            onInputChange={(_, value, reason) => {
              if (reason === "input") setClienteInput(value);
            }}
            renderInput={(params) => (
              <TextField
                {...params} label="Cliente" required
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {buscandoCliente && <CircularProgress size={16} />}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />

          <Divider textAlign="left" sx={{ fontSize: "0.75rem" }}>Datos del reporte (opcional)</Divider>
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5 }}>
            <TextField
              size="small" type="number" label="Monto Contrato"
              value={form.monto_contrato} onChange={handleChange("monto_contrato")}
              inputProps={{ min: 0, step: "0.01" }}
            />
            <TextField
              size="small" label="Potencia Instalada W"
              value={form.potencia_instalada} onChange={handleChange("potencia_instalada")}
            />
            <TextField
              size="small" label="Gestión"
              value={form.gestion} onChange={handleChange("gestion")}
            />
            <TextField
              size="small" label="Respaldo"
              value={form.respaldo} onChange={handleChange("respaldo")}
            />
            <TextField
              size="small" label="Responsable Técnico" sx={{ gridColumn: "1 / -1" }}
              value={form.responsable_tecnico} onChange={handleChange("responsable_tecnico")}
            />
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancelar</Button>
        <Button variant="contained" onClick={handleGuardar} disabled={!puedeGuardar}>
          {saving ? "Guardando..." : "Guardar"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RegistrarProyectoDialog;
